import { Duration } from 'luxon';
import { FC, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { CloseButton, StyledDialog } from '@/components/AlertDialogs/Dialog';
import { Modal } from '@/components/Modal';
import Text from '@/components/Text';
import { MediaResource, VoiceoverMedia } from '@/types/resources';

import {
  Audio,
  PlayButton,
  Player,
  TimePlayed,
  TotalTime,
  Track,
  calculateTime,
} from './AudioPlayer';

const Dialog = styled(StyledDialog)`
  width: 888px;
  padding: 16px;

  ${CloseButton} {
    top: 13px;
    right: 16px;
  }

  ${Track} {
    cursor: auto;
  }
`;

const Video = styled.video`
  width: 100%;
  height: 480px;

  &::-webkit-media-controls-panel {
    background-image: linear-gradient(transparent, transparent) !important;
  }
`;

const Header = styled.div`
  margin-bottom: 16px;
  display: flex;
`;

interface Props {
  voiceoverMedia: VoiceoverMedia;
  videoMedia: MediaResource;
  timestamp: string;
  onClose: VoidFunction;
  videoVolume: number;
}

const VideoAudioModal: FC<Props> = ({
  voiceoverMedia,
  videoMedia,
  timestamp,
  onClose,
  videoVolume,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [duration, setDuration] = useState<number>();
  const [currentTime, setCurrentTime] = useState(0);

  const startTimestamp = timestamp.split(' - ')[0].split(':');

  const startTime = Duration.fromObject({
    minutes: parseInt(startTimestamp[0]),
    seconds: parseInt(startTimestamp[1]),
  }).seconds;

  const playedPercentage = !duration ? 0 : (currentTime / duration) * 100;

  const onPlay = () => {
    setIsPlaying(true);

    videoRef.current!.currentTime = startTime;
    audioRef.current!.play();
    videoRef.current!.play();
  };

  useEffect(() => {
    if (!audioRef.current || !videoRef.current) {
      return;
    }

    const { current: audio } = audioRef;
    const { current: video } = videoRef;

    video.currentTime = startTime;

    const loadedmetadataCallback = () => {
      setDuration(audio.duration);
    };

    const timeupdateCallback = () => {
      setCurrentTime(audio.currentTime);

      if (audio.currentTime >= audio.duration) {
        video.pause();
        setIsPlaying(false);
      }
    };

    const onLoadedData = () => setIsReady(video.readyState === 4);

    video.addEventListener('loadeddata', onLoadedData);
    audio.addEventListener('loadedmetadata', loadedmetadataCallback);
    audio.addEventListener('timeupdate', timeupdateCallback);

    return () => {
      video.removeEventListener('loadeddata', onLoadedData);
      audio.removeEventListener('loadedmetadata', loadedmetadataCallback);
      audio.removeEventListener('timeupdate', timeupdateCallback);
    };
  }, [startTime]);

  useEffect(() => {
    if (!audioRef.current || !videoRef.current) {
      return;
    }

    audioRef.current.volume = 1;
    videoRef.current.volume = videoVolume;
  }, [videoVolume]);

  return (
    <Modal zIndex={50}>
      <Dialog
        initial={{ scale: 0.3 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.3 }}
      >
        <Header>
          <Text $type="caption" $color="gray">
            Timestamp ·{' '}
            <Text as="span" $type="caption">
              {timestamp}
            </Text>
          </Text>
        </Header>
        <Video ref={videoRef} preload="metadata">
          <source src={videoMedia.versions.original} type="video/mp4" />
          Your browser does not support the video tag.
        </Video>
        <Player>
          <PlayButton
            type="button"
            onClick={onPlay}
            disabled={isPlaying || !isReady}
          >
            {isPlaying ? (
              <svg
                width="6"
                height="8"
                viewBox="0 0 6 8"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect width="2" height="8" fill="currentColor" />
                <rect x="4" width="2" height="8" fill="currentColor" />
              </svg>
            ) : (
              <svg
                width="9"
                height="10"
                viewBox="0 0 9 10"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M8.33301 4.9987L0.333008 9.66536L0.333008 0.332031L8.33301 4.9987Z"
                  fill="currentColor"
                />
              </svg>
            )}
          </PlayButton>
          <Track $playedPercentage={playedPercentage}>
            {!!duration && (
              <>
                <TimePlayed $type="extraSmall" as="div">
                  {calculateTime(currentTime)}
                </TimePlayed>
                <TotalTime $type="extraSmall" as="div">
                  {calculateTime(duration)}
                </TotalTime>
              </>
            )}
          </Track>
          <Audio ref={audioRef} preload="metadata">
            <source src={voiceoverMedia.versions.original} type="audio/mpeg" />
          </Audio>
        </Player>
        <CloseButton onClick={onClose}>
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M13 1L1 13"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M13 13L1 1"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </CloseButton>
      </Dialog>
    </Modal>
  );
};

export default VideoAudioModal;
